import React, { useState, useEffect } from 'react';
import { 
  MicrophoneIcon, 
  BeakerIcon, 
  ClipboardDocumentIcon,
  ArrowPathIcon,
  CameraIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

interface MobileQuickInputProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  stacks: any[];
}

interface OperationData {
  operation: 'feeding' | 'harvest';
  stackId: number;
  data: any;
  completed: boolean;
  photo?: string;
}

const MobileQuickInput: React.FC<MobileQuickInputProps> = ({
  isOpen,
  onClose,
  onSubmit,
  stacks = []
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOperation, setSelectedOperation] = useState<string>('');
  const [currentStackIndex, setCurrentStackIndex] = useState(0);
  const [operationData, setOperationData] = useState<OperationData[]>([]);
  const [currentData, setCurrentData] = useState<any>({});
  const [showNumPad, setShowNumPad] = useState(false);
  const [currentField, setCurrentField] = useState<string>('');
  const [voiceActive, setVoiceActive] = useState(false);

  // Варианты операций
  const operations = [
    {
      id: 'feeding',
      title: 'Кормление',
      icon: BeakerIcon,
      color: 'bg-green-500',
      emoji: '🥗'
    },
    {
      id: 'harvest',
      title: 'Биогумус',
      icon: ArrowPathIcon,
      color: 'bg-yellow-500',
      emoji: '🌱'
    }
  ];

  // Варианты кормов для быстрого выбора
  const feedTypes = [
    { id: 1, name: 'Отруби пшеничные', emoji: '🌾' },
    { id: 2, name: 'Пшеница дробленая', emoji: '🌾' },
    { id: 3, name: 'Жмых подсолнечника', emoji: '🌻' },
    { id: 4, name: 'Смешанный корм', emoji: '🥣' }
  ];

  // Быстрые значения количества
  const quickAmounts = [0.5, 1.0, 1.5, 2.0, 2.5, 3.0];

  // Сброс состояния при открытии
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setSelectedOperation('');
      setCurrentStackIndex(0);
      setOperationData([]);
      setCurrentData({});
      setShowNumPad(false);
    }
  }, [isOpen]);

  // Обработка выбора операции
  const handleOperationSelect = (operationId: string) => {
    setSelectedOperation(operationId);
    setCurrentStep(1);
    
    // Инициализируем данные для всех стеллажей
    const initData = stacks.map(stack => ({
      operation: operationId as any,
      stackId: stack.id,
      data: {},
      completed: false
    }));
    setOperationData(initData);
  };

  // Переход к следующему стеллажу
  const handleNextStack = () => {
    // Сохраняем данные текущего стеллажа
    if (currentStackIndex < operationData.length) {
      const updatedData = [...operationData];
      updatedData[currentStackIndex] = {
        ...updatedData[currentStackIndex],
        data: currentData,
        completed: Object.keys(currentData).length > 0
      };
      setOperationData(updatedData);
    }

    if (currentStackIndex < stacks.length - 1) {
      setCurrentStackIndex(currentStackIndex + 1);
      // Загружаем данные следующего стеллажа
      const nextData = operationData[currentStackIndex + 1]?.data || {};
      setCurrentData(nextData);
    } else {
      // Завершаем ввод
      handleComplete();
    }
  };

  // Переход к предыдущему стеллажу
  const handlePrevStack = () => {
    if (currentStackIndex > 0) {
      setCurrentStackIndex(currentStackIndex - 1);
      const prevData = operationData[currentStackIndex - 1]?.data || {};
      setCurrentData(prevData);
    }
  };

  // Пропуск текущего стеллажа
  const handleSkipStack = () => {
    const updatedData = [...operationData];
    updatedData[currentStackIndex] = {
      ...updatedData[currentStackIndex],
      completed: false,
      data: {}
    };
    setOperationData(updatedData);
    handleNextStack();
  };

  // Завершение ввода
  const handleComplete = () => {
    // Сохраняем данные последнего стеллажа
    const finalData = [...operationData];
    finalData[currentStackIndex] = {
      ...finalData[currentStackIndex],
      data: currentData,
      completed: Object.keys(currentData).length > 0
    };
    
    onSubmit(finalData);
    onClose();
  };

  // Ввод в числовое поле
  const handleNumPadValue = (value: string) => {
    if (currentField) {
      setCurrentData({
        ...currentData,
        [currentField]: value
      });
      setShowNumPad(false);
      setCurrentField('');
    }
  };

  // Голосовой ввод
  const handleVoiceInput = () => {
    setVoiceActive(true);
    // TODO: Интеграция с Speech Recognition API
    setTimeout(() => {
      setVoiceActive(false);
      // Имитация распознанной команды
      alert('Голосовой ввод: "Полтора килограмма отрубей, активность средняя"');
    }, 3000);
  };

  // Фото
  const handlePhoto = () => {
    // TODO: Интеграция с камерой
    alert('Функция фотографирования будет добавлена');
  };

  if (!isOpen) return null;

  const currentStack = stacks[currentStackIndex];
  const completedStacks = operationData.filter(d => d.completed).length;
  const progressPercent = (completedStacks / stacks.length) * 100;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Заголовок */}
      <div className="bg-green-500 text-white p-4 flex justify-between items-center">
        <h2 className="text-xl font-bold">🚀 Быстрый ввод</h2>
        <button onClick={onClose} className="p-2">
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Прогресс */}
      {currentStep > 0 && (
        <div className="bg-gray-100 p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">
              {currentStack?.name || 'Стеллаж'} ({currentStackIndex + 1} из {stacks.length})
            </span>
            <span className="text-sm text-gray-600">
              {completedStacks} выполнено
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Контент */}
      <div className="flex-1 overflow-y-auto">
        
        {/* Шаг 1: Выбор операции */}
        {currentStep === 0 && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-6 text-center">
              Выберите операцию
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {operations.map((operation) => (
                <button
                  key={operation.id}
                  onClick={() => handleOperationSelect(operation.id)}
                  className={`${operation.color} text-white p-6 rounded-xl flex flex-col items-center space-y-3 hover:scale-105 transform transition-all duration-200 shadow-lg`}
                >
                  <span className="text-4xl">{operation.emoji}</span>
                  <span className="font-medium text-lg">{operation.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Шаг 2: Ввод данных по стеллажам */}
        {currentStep === 1 && selectedOperation && (
          <div className="p-6 space-y-6">
            
            {/* Информация о стеллаже */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">
                📦 {currentStack?.name}
              </h3>
              <div className="text-sm text-gray-600">
                <p>Объем: {currentStack?.volume_liters || 200} л</p>
                <p>Расположение: {currentStack?.location || 'Основное помещение'}</p>
              </div>
            </div>

            {/* Формы для разных операций */}
            
            {/* Кормление */}
            {selectedOperation === 'feeding' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Тип корма</label>
                  <div className="grid grid-cols-2 gap-2">
                    {feedTypes.map((feed) => (
                      <button
                        key={feed.id}
                        onClick={() => setCurrentData({ ...currentData, feedTypeId: feed.id, feedType: feed.name })}
                        className={`p-3 rounded-lg border-2 flex items-center space-x-2 ${
                          currentData.feedTypeId === feed.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-lg">{feed.emoji}</span>
                        <span className="text-sm">{feed.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Количество (кг)</label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {quickAmounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setCurrentData({ ...currentData, amount })}
                        className={`p-3 rounded-lg border-2 text-center ${
                          currentData.amount === amount
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {amount} кг
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setCurrentField('amount');
                      setShowNumPad(true);
                    }}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600"
                  >
                    Другое количество: {currentData.amount || '?'} кг
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Активность поедания</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Высокая', 'Средняя', 'Низкая'].map((activity) => (
                      <button
                        key={activity}
                        onClick={() => setCurrentData({ ...currentData, activity })}
                        className={`p-3 rounded-lg border-2 text-center ${
                          currentData.activity === activity
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {activity}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}



            {/* Сбор биогумуса */}
            {selectedOperation === 'harvest' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Объем собранного (л)</label>
                  <button
                    onClick={() => {
                      setCurrentField('volume');
                      setShowNumPad(true);
                    }}
                    className="w-full p-4 border-2 border-gray-200 rounded-lg text-left"
                  >
                    {currentData.volume || 'Введите объем'} л
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Качество</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Премиум', 'Стандарт', 'Низкое'].map((quality) => (
                      <button
                        key={quality}
                        onClick={() => setCurrentData({ ...currentData, quality })}
                        className={`p-3 rounded-lg border-2 text-center ${
                          currentData.quality === quality
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {quality}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Нижняя панель с действиями */}
      {currentStep === 1 && (
        <div className="bg-white border-t border-gray-200 p-4 space-y-4">
          
          {/* Дополнительные действия */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleVoiceInput}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                voiceActive 
                  ? 'bg-red-500 text-white' 
                  : 'bg-blue-500 text-white'
              }`}
              disabled={voiceActive}
            >
              <MicrophoneIcon className="w-5 h-5" />
              <span>{voiceActive ? 'Слушаю...' : 'Голос'}</span>
            </button>
            
            <button
              onClick={handlePhoto}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg flex items-center space-x-2"
            >
              <CameraIcon className="w-5 h-5" />
              <span>Фото</span>
            </button>
          </div>

          {/* Навигация */}
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrevStack}
              disabled={currentStackIndex === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              <span>Назад</span>
            </button>

            <button
              onClick={handleSkipStack}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg"
            >
              Пропустить
            </button>

            <button
              onClick={handleNextStack}
              className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg"
            >
              <span>{currentStackIndex === stacks.length - 1 ? 'Завершить' : 'Далее'}</span>
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Числовая клавиатура */}
      {showNumPad && (
        <div className="absolute inset-x-0 bottom-0 bg-white border-t border-gray-200 p-4">
          <div className="mb-4 text-center">
            <p className="text-sm text-gray-600">Введите значение для: {currentField}</p>
            <p className="text-2xl font-bold">{currentData[currentField] || '0'}</p>
          </div>
          
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                onClick={() => {
                  const current = currentData[currentField] || '';
                  setCurrentData({
                    ...currentData,
                    [currentField]: current + num.toString()
                  });
                }}
                className="p-4 bg-gray-100 rounded-lg text-xl font-semibold"
              >
                {num}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => {
                const current = currentData[currentField] || '';
                if (!current.includes('.')) {
                  setCurrentData({
                    ...currentData,
                    [currentField]: current + '.'
                  });
                }
              }}
              className="p-4 bg-gray-100 rounded-lg text-xl font-semibold"
            >
              .
            </button>
            
            <button
              onClick={() => {
                const current = currentData[currentField] || '';
                setCurrentData({
                  ...currentData,
                  [currentField]: current + '0'
                });
              }}
              className="p-4 bg-gray-100 rounded-lg text-xl font-semibold"
            >
              0
            </button>
            
            <button
              onClick={() => {
                const current = currentData[currentField] || '';
                setCurrentData({
                  ...currentData,
                  [currentField]: current.slice(0, -1)
                });
              }}
              className="p-4 bg-red-100 rounded-lg text-xl font-semibold text-red-600"
            >
              ⌫
            </button>
          </div>
          
          <div className="flex space-x-2 mt-4">
            <button
              onClick={() => {
                setShowNumPad(false);
                setCurrentField('');
              }}
              className="flex-1 p-3 bg-gray-200 rounded-lg"
            >
              Отмена
            </button>
            
            <button
              onClick={() => {
                setShowNumPad(false);
                setCurrentField('');
              }}
              className="flex-1 p-3 bg-green-500 text-white rounded-lg"
            >
              Готово
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileQuickInput;
